/**
 * Expanded Device Details (DeviceRow) shows Fleet-confirmed Volume / Master /
 * Wake-Sleep / App Lock. Expand starts with one report_device_info
 * ("Updating from device…"), then must keep reading Fleet GET while
 * open so device-originated Option B posts appear without a browser refresh.
 *
 * Remote Console (SettingsForm) already live-polls separately — do not change it.
 *
 * Collapsed rows stay at 0 — inventory idle-polling doctrine unchanged.
 * Reuses the Remote Settings live interval so both surfaces match cadence.
 */

import { REMOTE_SETTINGS_LIVE_REFRESH_MS } from "@/features/devices/remoteSettingsLiveRefresh";

export const DEVICE_INFO_LIVE_REFRESH_MS = REMOTE_SETTINGS_LIVE_REFRESH_MS;

export type DeviceInfoLiveRefreshInput = {
  expanded: boolean;
};

export function decideDeviceInfoLiveRefreshMs(
  input: DeviceInfoLiveRefreshInput
): number {
  if (!input.expanded) return 0;
  return DEVICE_INFO_LIVE_REFRESH_MS;
}

export type DeviceInfoLivePlayerSettings = {
  appLock?: boolean | null;
  volume?: number | null;
  masterLevel?: number | null;
  wakeSleepEnabled?: boolean | null;
  /** Display string e.g. "08:00 / 00:00" or Off */
  wakeSleep?: string | null;
  startTime?: string | null;
  endTime?: string | null;
};

/**
 * Whether a new devicesGet payload should replace the displayed confirmed
 * player fields on the expanded Device Details panel.
 * Pure Fleet freshness — no form touched-state (Device Details is read-only).
 */
export function shouldApplyDeviceInfoLiveSettings(input: {
  expanded: boolean;
  incomingSyncedAt: string | null | undefined;
  lastAppliedSyncedAt: string | null | undefined;
  incoming?: DeviceInfoLivePlayerSettings | null;
  displayed?: DeviceInfoLivePlayerSettings | null;
}): boolean {
  if (!input.expanded) return false;
  if (!input.incomingSyncedAt) return false;
  if (input.incomingSyncedAt === input.lastAppliedSyncedAt) {
    // Same sync timestamp: still apply if any confirmed field drifted
    // (defensive; normal Option B always bumps syncedAt).
    if (playerSettingsDrifted(input.incoming, input.displayed)) {
      return true;
    }
    return false;
  }
  if (
    input.lastAppliedSyncedAt &&
    input.incomingSyncedAt < input.lastAppliedSyncedAt
  ) {
    return false;
  }
  return true;
}

function playerSettingsDrifted(
  incoming?: DeviceInfoLivePlayerSettings | null,
  displayed?: DeviceInfoLivePlayerSettings | null
): boolean {
  if (!incoming || !displayed) return false;
  const keys: (keyof DeviceInfoLivePlayerSettings)[] = [
    "appLock",
    "volume",
    "masterLevel",
    "wakeSleepEnabled",
    "wakeSleep",
    "startTime",
    "endTime",
  ];
  for (const key of keys) {
    const a = incoming[key];
    const b = displayed[key];
    if (a === undefined || a === null || b === undefined || b === null) continue;
    if (a !== b) return true;
  }
  return false;
}
